import {useCallback, useEffect, useState} from 'react'
import {useLocationWorking} from '@/utils/locationSignal'

/**
 * Browser location permission as a THING YOU CAN ASK FOR, separate from any one screen.
 *
 * ⚠️ THE SAME HOOK AS THE DRIVER PORTAL'S (hooks/useLocationAccess.js there) — one answer
 * to "is location allowed?" for every screen, with the iOS Safari handling. Keep the two
 * in step.
 *
 * ── Why this is its own hook ──────────────────────────────────────────────────────
 * `useDriverLocation` already reads the permission, but it is bound to a trip: it takes a
 * `tripId`, reports to that trip's endpoint, and only runs on the en-route screen. Going
 * ONLINE has the same requirement and none of that context — a driver on the dashboard with
 * no ride still has to be locatable, or dispatch is ranking a queue of cars it cannot see.
 *
 * So the permission question lives here, and both callers ask it.
 *
 * `state` mirrors the browser:
 *   'checking'  consulting the Permissions API — decide nothing yet
 *   'prompt'    not answered; `request()` will raise the native dialog
 *   'granted'   allowed, and a position has been read
 *   'denied'    refused, or the device has no geolocation at all
 *
 * ⚠️ 'granted' MEANS THE BROWSER SAYS SO — and it USED TO mean "a fix was actually read".
 * That was the stricter, better-sounding rule and it had to go: see `permittedRef` below.
 * A grant is consent, and consent does not evaporate because a car park has no sky. A fix
 * is still read on top of it, and a REFUSAL (code 1) still takes the grant away; what no
 * longer takes it away is a slow satellite.
 *
 * ⚠️ AND IT KEEPS WATCHING. `PermissionStatus.onchange` fires when a driver revokes access
 * from the address bar mid-shift, which is exactly the case the gate exists for — a
 * permission checked once at sign-on is not a permission held all day.
 */
/**
 * iPhone / iPad — every browser there is WebKit, whatever its name.
 *
 * ⚠️ WHY iOS IS PROBED. Safari's Permissions API is not a reliable "is location allowed":
 * it reports "prompt" for a site the driver ALLOWED in Settings, and older iOS has no
 * Permissions API at all. Reading only the flag, a driver who switched location on in
 * Settings stayed behind the location modal forever. So on iOS a "prompt" is checked by
 * actually reading a position: allowed in Settings → it resolves silently and the state
 * becomes 'granted'; blocked → code 1 → 'denied'.
 */
const IS_IOS = typeof navigator !== 'undefined'
    && (/iPad|iPhone|iPod/.test(navigator.userAgent)
        || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1))

/* One probe at a time for the whole app — several components use this hook at once, and
   each asking separately would be several reads for one answer. */
let iosProbe = null
function probeIos() {
    if (!iosProbe) {
        iosProbe = new Promise((resolve) => {
            navigator.geolocation.getCurrentPosition(
                () => resolve('granted'),
                (err) => resolve(err.code === 1 ? 'denied' : 'unknown'),
                {enableHighAccuracy: false, timeout: 10000, maximumAge: 60000},
            )
        }).finally(() => { setTimeout(() => { iosProbe = null }, 0) })
    }
    return iosProbe
}

/**
 * ⚠️ ONE STATE FOR THE WHOLE APP, not one per caller. Every component used to hold its own
 * copy, and they could disagree: OnDutyLocation's copy read "denied" (a fix refused with
 * code 1) and took the driver offline, while LocationPrompt's copy still said "granted" —
 * so the location modal closed the moment the toggle went off. Now whatever any caller
 * learns, every caller sees.
 */
let sharedState = 'checking'
const subscribers = new Set()
function setSharedState(next) {
    if (sharedState === next) return
    sharedState = next
    subscribers.forEach((fn) => fn(next))
}
// Consent, held apart from whether a fix has arrived — see the note in the hook. Shared
// for the same reason as the state.
const sharedPermitted = {current: false}

export function useLocationAccess() {
    const [state, setLocalState] = useState(sharedState)
    const setState = setSharedState

    useEffect(() => {
        subscribers.add(setLocalState)
        setLocalState(sharedState)
        return () => { subscribers.delete(setLocalState) }
    }, [])

    /**
     * HAS THE BROWSER TOLD US `granted`? — consent, held apart from whether a position has
     * actually arrived.
     *
     * ⚠️ THIS IS THE FIX FOR A DRIVER WHO COULD NOT GO ONLINE WITH LOCATION SWITCHED ON.
     * `read` used to demote any failed fix to 'prompt', including codes 2 and 3 — position
     * unavailable and TIMEOUT. A granted driver whose GPS was cold, indoors, or simply
     * slower than the 15s timeout therefore failed the gate, was shown "share your location
     * to go online" about a permission they had already given, and had no way through it:
     * pressing again started another 15s wait that failed the same way.
     *
     * `useDriverLocation` has carried the same guard for the trip path for some time — see
     * "⚠️ A GRANTED PERMISSION SURVIVES A FAILED FIX" there. This hook was never updated to
     * match, and the online toggle is the one place that difference was fatal.
     *
     * PERMISSION and A FIX are two different facts. Only a code 1 changes the first.
     */
    const permittedRef = sharedPermitted

    /** Read a position for real. Resolves true only if one arrives. */
    const read = useCallback(() => new Promise((resolve) => {
        if (typeof navigator === 'undefined' || !navigator.geolocation) {
            permittedRef.current = false
            setState('denied')
            resolve(false)
            return
        }
        navigator.geolocation.getCurrentPosition(
            () => { permittedRef.current = true; setState('granted'); resolve(true) },
            (err) => {
                // 1 = PERMISSION_DENIED. The other two (position unavailable, timeout) are
                // NOT a refusal — a driver in an underground car park has not said no, and
                // telling them they have is how a working permission gets reported as broken.
                if (err.code === 1) {
                    permittedRef.current = false
                    setState('denied')
                    resolve(false)
                    return
                }

                /* ⚠️ A KNOWN-PERMITTED DRIVER STAYS GRANTED THROUGH A FAILED FIX, and the
                   promise still resolves TRUE. The caller is a gate on going online, and
                   the question it is really asking is "may we locate this driver?", not
                   "is a satellite lock available this second". The ambient pinger retries
                   for as long as the shift lasts; refusing the shift over one slow fix
                   helps nobody. */
                if (permittedRef.current) {
                    setState('granted')
                    resolve(true)
                    return
                }

                setState('prompt')
                resolve(false)
            },
            {enableHighAccuracy: true, timeout: 15000, maximumAge: 0},
        )
    }), [])

    /** The gate. Raises the native prompt when it has not been answered. */
    const request = useCallback(() => read(), [read])

    useEffect(() => {
        let cancelled = false

        if (typeof navigator === 'undefined' || !navigator.geolocation) {
            setState('denied')
            return undefined
        }
        // An iOS "prompt" is checked by reading a position — see IS_IOS.
        const probe = () => {
            probeIos().then((result) => {
                if (cancelled) return
                if (result === 'granted') {
                    permittedRef.current = true
                    setState('granted')
                } else if (result === 'denied') {
                    permittedRef.current = false
                    setState('denied')
                } else if (!permittedRef.current) {
                    setState('prompt')
                }
            })
        }

        if (!navigator.permissions?.query) {
            // No Permissions API (older Safari). On iOS the answer comes from a read — see
            // IS_IOS; elsewhere from `request()` when the driver acts.
            if (IS_IOS) probe()
            else setState('prompt')
            return undefined
        }

        /* ⚠️ `permittedRef` IS SET FROM THE FLAG, and the state with it — see `read`. The
           browser saying `granted` is the consent; the fix that follows is only
           confirmation, and a driver must not be held at 'checking' while it is fetched.
           `read()` still runs, and still upgrades nothing that is not already true. */
        const apply = (permission) => {
            if (permission === 'granted') {
                permittedRef.current = true
                setState('granted')
                read()
                return
            }
            if (permission === 'denied') {
                permittedRef.current = false
                setState('denied')
                return
            }
            /* ⚠️ "prompt" DOES NOT REVOKE A WORKING LOCATION — mobile Safari (and Chrome at
               times) report "prompt" while fixes are arriving. Same rule as
               `useDriverLocation`. */
            if (!permittedRef.current) {
                if (IS_IOS) probe()
                else setState('prompt')
            }
        }

        let status = null

        /**
         * ⚠️ RE-ASKED WHEN THE TAB COMES BACK, because `onchange` CANNOT BE RELIED ON.
         *
         * The driver allows location in the browser's own site-settings sheet — which on
         * Android Chrome means leaving the page, flipping a switch and returning. That
         * sheet does not always fire `PermissionStatus.onchange`, so the portal went on
         * believing the permission was blocked: the prompt reappeared on every navigation,
         * the driver dismissed it every time, and only a RELOAD ever cleared it. Reported
         * as "it is checking that on reload only", which is exactly what it was.
         *
         * Returning to the tab is the one moment we know they may have just changed it, so
         * that is when to look again. `visibilitychange` covers the sheet and app switching;
         * `focus` covers a desktop settings window.
         */
        const recheck = () => {
            if (cancelled || document.visibilityState !== 'visible') return
            navigator.permissions.query({name: 'geolocation'})
                .then((result) => { if (!cancelled) apply(result.state) })
                .catch(() => {})
        }

        navigator.permissions.query({name: 'geolocation'})
            .then((result) => {
                if (cancelled) return
                status = result
                apply(result.state)

                // Revoked mid-shift must be noticed — see the note at the top.
                result.onchange = () => {
                    if (cancelled) return
                    apply(result.state)
                }
            })
            .catch(() => {
                if (cancelled) return
                if (IS_IOS) probe()
                else setState('prompt')
            })

        document.addEventListener('visibilitychange', recheck)
        window.addEventListener('focus', recheck)

        return () => {
            cancelled = true
            if (status) status.onchange = null
            document.removeEventListener('visibilitychange', recheck)
            window.removeEventListener('focus', recheck)
        }
    }, [read])

    /* Passenger side: a fix that actually arrived anywhere in the app (the tracking map,
       walk-to-pickup — utils/locationSignal) is proof of a grant on every browser. */
    const working = useLocationWorking()
    const effective = working && state !== 'denied' ? 'granted' : state

    return {state: effective, isGranted: effective === 'granted', request}
}

export default useLocationAccess
